import React, { useState } from 'react';
import Dialog, {
	DialogTitle,
	DialogContent,
	DialogActions,
} from '../common/Dialog';
import Button from '../common/buttons/Button';
import { Typography, TextField } from '@mui/material';
import { CompanyExternalUser } from '../../data/models/companyExternalUser';

interface ResendInvitationConfirmationModalProps {
	open: boolean;
	handleClose: () => void;
	user?: CompanyExternalUser;
	users?: CompanyExternalUser[];
	onConfirm: (customMessage?: string) => void;
	isInitialInvitation?: boolean;
}

const ResendInvitationConfirmationModal: React.FC<
	ResendInvitationConfirmationModalProps
> = ({
	open,
	handleClose,
	user,
	users,
	onConfirm,
	isInitialInvitation = false,
}) => {
	const [customMessage, setCustomMessage] = useState<string>('');

	// If neither user nor users are provided, don't render
	if (!user && (!users || users.length === 0)) return null;

	const isBulkAction = !user && users && users.length > 0;
	const userCount = isBulkAction ? users!.length : 1;

	const handleConfirm = () => {
		onConfirm(customMessage || undefined);
		handleClose();
	};

	const handleCloseModal = () => {
		setCustomMessage('');
		handleClose();
	};

	const modalTitle = isInitialInvitation
		? `Send Invitation${isBulkAction ? 's' : ''}`
		: `Resend Invitation${isBulkAction ? 's' : ''}`;
	const actionText = isInitialInvitation ? 'send' : 'resend';

	return (
		<Dialog open={open} handleClose={handleCloseModal}>
			<DialogTitle>{modalTitle}</DialogTitle>
			<DialogContent>
				<Typography variant="body1" gutterBottom>
					{isBulkAction ? (
						<>
							Are you sure you want to {actionText} invitations to{' '}
							<strong>{userCount} selected users</strong>?
						</>
					) : (
						<>
							Are you sure you want to {actionText} an invitation
							to{' '}
							<strong>
								{user!.firstName} {user!.lastName}
							</strong>{' '}
							({user!.email})?
						</>
					)}
				</Typography>
				{!isInitialInvitation && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 2 }}
					>
						This will deactivate{' '}
						{isBulkAction
							? 'the previous invitation links and send new emails'
							: 'the previous invitation link and send a new email'}{' '}
						with{' '}
						{isBulkAction
							? 'fresh invitation links and temporary passwords'
							: 'a fresh invitation link and temporary password'}
						.
					</Typography>
				)}
				{isInitialInvitation && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 2 }}
					>
						This will send {isBulkAction ? 'emails' : 'an email'}{' '}
						with{' '}
						{isBulkAction
							? 'invitation links and temporary passwords'
							: 'an invitation link and temporary password'}
						.
					</Typography>
				)}
				<TextField
					label="Custom Message (Optional)"
					multiline
					rows={3}
					value={customMessage}
					onChange={(e) => setCustomMessage(e.target.value)}
					placeholder="Enter a custom message to include with the invitation..."
					fullWidth
					sx={{ mt: 2 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} variant="text" color="inherit">
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color="primary"
				>
					{isInitialInvitation ? 'Send' : 'Resend'} Invitation
					{isBulkAction ? 's' : ''}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ResendInvitationConfirmationModal;
